import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import { useAuth } from '../constants/providers/AuthProvider';

export interface AppNotification {
  id: string;
  user_id: string;
  company_id: string | null;
  type:
    | 'NEW_MESSAGE'
    | 'POST_COMMENT'
    | 'POST_REACTION'
    | 'CLASS_REMINDER'
    | 'BILLING_REMINDER'
    | 'SUBSCRIPTION_INVITATION'
    | 'GYM_ANNOUNCEMENT'
    | 'ATTENDANCE_CONFIRMATION'
    | 'PAYMENT_CONFIRMATION'
    | 'CLASS_CANCELLED'
    | 'CLASS_RESCHEDULED'
    | 'subscription_enrollment'
    | 'subscription_cancelled'
    | 'subscription_expired'
    | 'payment_due'
    | 'payment_received'
    | 'account_note'
    | 'system_announcement'
    | 'TRIAL_SUBSCRIPTION_REQUEST'
    | 'TRIAL_EXPIRING'
    | 'AGE_EXCEPTION_REQUEST';
  title: string;
  body: string;
  data: Record<string, unknown>;
  is_read: boolean | null; // Can be null for legacy notifications or edge cases
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch user notifications
 */
export function useNotificationsQuery() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: queryKeys.notifications.list(userId),
    enabled: !!userId,
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return (data || []) as AppNotification[];
    },
  });
}

/**
 * Hook to get unread notifications count
 */
export function useUnreadNotificationsCount() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(userId),
    enabled: !!userId,
    staleTime: 10_000, // 10 seconds
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!userId) return 0;

      // Count unread notifications (is_read is false or null)
      // Use .or() to handle both false and null values
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .or('is_read.eq.false,is_read.is.null');

      if (error) throw error;
      return count || 0;
    },
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useMutation({
    mutationFn: async (notificationId: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('mark_notification_read', {
        p_notification_id: notificationId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User ID is required');
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('mark_all_notifications_read', {
        p_user_id: userId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
    },
  });
}

/**
 * Hook to approve a trial subscription request
 */
export function useApproveTrialSubscription() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useMutation({
    mutationFn: async ({ enrollmentId }: { enrollmentId: string }) => {
      if (!userId) throw new Error('User ID is required');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('approve_trial_subscription', {
        p_enrollment_id: enrollmentId,
        p_approved_by: userId,
        p_approved: true,
        p_rejection_reason: null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
      // Invalidate subscription enrollments to refresh data
      queryClient.invalidateQueries({ queryKey: ['subscription_enrollments'] });
    },
  });
}

/**
 * Hook to reject a trial subscription request
 */
export function useRejectTrialSubscription() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useMutation({
    mutationFn: async ({ enrollmentId, reason }: { enrollmentId: string; reason: string }) => {
      if (!userId) throw new Error('User ID is required');
      if (!reason.trim()) throw new Error('Rejection reason is required');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('approve_trial_subscription', {
        p_enrollment_id: enrollmentId,
        p_approved_by: userId,
        p_approved: false,
        p_rejection_reason: reason.trim(),
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
      // Invalidate subscription enrollments to refresh data
      queryClient.invalidateQueries({ queryKey: ['subscription_enrollments'] });
    },
  });
}

/**
 * Hook to accept a subscription invitation
 */
export function useAcceptSubscriptionInvitation() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useMutation({
    mutationFn: async ({ invitationId }: { invitationId: string }) => {
      if (!userId) throw new Error('User ID is required');

      // First, get the invitation to check if company switching is needed
      const { data: invitation, error: fetchError } = await supabase
        .from('subscription_invitations')
        .select('id, company_id, status, company_subscriptions(subscription_type)')
        .eq('id', invitationId)
        .single();

      if (fetchError || !invitation) {
        throw new Error(fetchError?.message || 'Failed to fetch invitation details');
      }

      // Check if already accepted
      if (invitation.status === 'accepted') {
        return { success: true, already_accepted: true };
      }

      // Switch company if invitation is from a different company
      const { data: { user } } = await supabase.auth.getUser();
      if (user && invitation.company_id) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('last_selected_company')
          .eq('id', user.id)
          .single();

        if (userProfile?.last_selected_company !== invitation.company_id) {
          await supabase
            .from('user_profiles')
            .update({ last_selected_company: invitation.company_id })
            .eq('id', user.id);
          
          // Refresh session to get updated JWT with new company_id
          await supabase.auth.refreshSession();
        }
      }

      // Accept the invitation
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('accept_subscription_invitation', {
        invitation_id: invitationId,
        p_trial_start_date: null,
      });

      if (error) throw error;
      if (data?.success === false) {
        throw new Error(data?.error || 'Failed to accept invitation');
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
      queryClient.invalidateQueries({ queryKey: ['subscription-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['user-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['child-subscriptions'] });
    },
  });
}

/**
 * Hook to reject a subscription invitation
 */
export function useRejectSubscriptionInvitation() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  return useMutation({
    mutationFn: async ({ invitationId }: { invitationId: string }) => {
      if (!userId) throw new Error('User ID is required');

      const { error } = await supabase
        .from('subscription_invitations')
        .update({
          status: 'rejected',
          responded_at: new Date().toISOString(),
        })
        .eq('id', invitationId)
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
      queryClient.invalidateQueries({ queryKey: ['subscription-invitations'] });
    },
  });
}
